import * as valibot from 'valibot'
import { DEFAULT_LOG_RANGE, LOG_RANGES } from 'shared/wide-event-logs'
import { Skeleton } from '@/components/ui/skeleton'
import { SETTINGS_STACK, SettingsSectionSkeleton } from '../settings/settings-section'

// The logs page's pending UI and the copy it shares with the loaded page (admin.logs.tsx). On the eager path: no
// icons, no explorer code.

/** Every event, the console lines only, or those lines grouped by browser session. */
export const LOG_VIEWS = ['all', 'console', 'sessions'] as const

/** A bad zoom bound falls back to the range on the server (resolveLogWindow). */
export const LOG_SEARCH_DEFAULTS = { query: '', range: DEFAULT_LOG_RANGE, live: false, view: 'all' } as const
export const LOG_SEARCH_FIELDS = {
  query: valibot.fallback(valibot.optional(valibot.string(), ''), ''),
  range: valibot.fallback(valibot.optional(valibot.picklist(LOG_RANGES), DEFAULT_LOG_RANGE), DEFAULT_LOG_RANGE),
  from: valibot.optional(valibot.string()),
  to: valibot.optional(valibot.string()),
  live: valibot.fallback(valibot.optional(valibot.boolean(), false), false),
}

export const LOGS_TITLE = 'Logs'
export const LOGS_DESCRIPTION =
  'Every wide event the app recorded — requests, page views, clicks, console lines, errors — on one timeline. Filter with the facets or the query, drag across the chart to zoom, open a row for all its attributes. Console keeps what the server and the browsers wrote to the console; By session reads one tab’s console from start to end.'

export function LogsPending() {
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSectionSkeleton title={LOGS_TITLE} description={LOGS_DESCRIPTION}>
        <div className="flex flex-col gap-4">
          <ExplorerToolbarSkeleton />
          <HistogramSkeleton />
          <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
            <FacetsSkeleton />
            <LogRowsSkeleton />
          </div>
        </div>
      </SettingsSectionSkeleton>
    </div>
  )
}

/** The query field and its Run button, the view tabs, the range tabs and the live toggle. */
function ExplorerToolbarSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-8 min-w-60 flex-1 rounded-lg" />
        <Skeleton className="h-9 w-64 rounded-lg" />
        <Skeleton className="h-9 w-64 rounded-lg" />
        <Skeleton className="h-9 w-20 rounded-lg" />
      </div>
      <Skeleton className="h-3.5 w-96 max-w-full" />
    </div>
  )
}

/** The count and legend line, the bars' box, the axis. */
export function HistogramSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-48" />
      </div>
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-3 w-full max-w-md" />
    </div>
  )
}

export function FacetsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[4, 5, 3, 6].map((rows, i) => (
        // oxlint-disable-next-line react/no-array-index-key -- a fixed placeholder list
        <div key={i} className="flex flex-col gap-1.5">
          <Skeleton className="h-3 w-16" />
          {Array.from({ length: rows }, (_, j) => (
            <Skeleton key={j} className="h-4 w-full" />
          ))}
        </div>
      ))}
    </div>
  )
}

/** The header row, then rows at the height of a one-line row. */
export function LogRowsSkeleton() {
  return (
    <div className="flex flex-col gap-px overflow-hidden rounded-xl border">
      <Skeleton className="h-7 rounded-none" />
      {Array.from({ length: 14 }, (_, i) => (
        <Skeleton key={i} className="h-7 rounded-none" />
      ))}
    </div>
  )
}
